import { useEffect, useRef, useState } from "react";
import { getPreferredAudioConstraints } from "../../../../audio-kernel/micPreference";

const VOLUME_THRESHOLD = 35; // 0-100, tune this if too sensitive
const SILENCE_DELAY_MS = 1200; // how long silence before glow turns off

const useVoiceActivity = (isRecording, interviewId) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const silenceTimerRef = useRef(null);

  useEffect(() => {
    if (!isRecording) {
      cleanup();
      setIsSpeaking(false);
      return;
    }

    let active = true;

    navigator.mediaDevices.getUserMedia({
      ...getPreferredAudioConstraints(interviewId),
      video: false,
    })
      .then((stream) => {
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }

        streamRef.current = stream;
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        const data = new Uint8Array(analyserRef.current.frequencyBinCount);

        const tick = () => {
          if (!active) return;
          analyserRef.current.getByteFrequencyData(data);
          const volume = data.reduce((a, b) => a + b, 0) / data.length;

          if (volume > VOLUME_THRESHOLD) {
            clearTimeout(silenceTimerRef.current);
            setIsSpeaking(true);
          } else {
            silenceTimerRef.current = setTimeout(() => {
              setIsSpeaking(false);
            }, SILENCE_DELAY_MS);
          }

          rafRef.current = requestAnimationFrame(tick);
        };

        tick();
      })
      .catch(() => {
        // mic access denied or unavailable — fail silently
      });

    return () => {
      active = false;
      cleanup();
    };
  }, [isRecording, interviewId]);

  const cleanup = () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(silenceTimerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  return isSpeaking;
};

export default useVoiceActivity;
